'use strict';

import BaseComponent from "./BaseComponent.js";

export default class Media extends BaseComponent{
	/**
	 * @type {Object<*>}
	 */
	#args;

	/**
	 * Default arguments for configuring media selection.
	 * @type {{
	 *  hiddenInput: (HTMLElement|null),
	 *  openButton: (HTMLElement|null),
	 *  removeButton: (HTMLElement|null),
	 *  previewElement: ({
	 *      container: (HTMLElement|null),
	 *      selector: string,
	 *      removeButtonSelector: string,
	 *      template: string
	 *  }|HTMLElement|null),
	 *  mediaHeading: (string|{select?: string, change?: string}),
	 *  multipleSelect: boolean,
	 *  mediaSelectButtonText: (string|{select?: string, change?: string}),
	 *  blankImage: string,
	 *  attachmentSize: string,
	 *  showSelectedMediaOnReOpen: boolean,
	 *  disableRemoveButton: boolean,
	 *  buttonText: (string|{select?: string, change?: string}),
	 *  onOpen: Function,
	 *  onSelect: Function,
	 *  onRemove: Function,
	 *  onRemoveSingle: Function,
	 *  onClose: Function
	 * }}
	 */
	#defaultArgs = {
		/**
		 * The hidden input element.
		 * @type {HTMLElement|null}
		 */
		hiddenInput: null,

		/**
		 * The button element to open the media selection.
		 * @type {HTMLElement|null}
		 */
		openButton: null,

		/**
		 * The button element to remove selected media.
		 * @type {HTMLElement|null}
		 */
		removeButton: null,

		/**
		 * The preview element or its configuration.
		 * @type {({
		 *     container: HTMLElement|null,
		 *     selector: string,
		 *     removeButtonSelector: string,
		 *     template: string
		 * }|HTMLElement|null)}
		 *
		 * @example previewElement: {
		 *	container: null, //Container of a preview elements
		 *	selector: 'img', //preview element selector
		 *	removeButtonSelector: 'button', //selector of a remove button for one image
		 * 	template: `<figure><img /><button>&times;</button></figure>`
		 * }
		 */
		previewElement: null,

		/**
		 * Heading text for media selection dialog or object with select and change text variations.
		 * @type {string|{select?: string, change?: string}}
		 *
		 * @example mediaHeading: {select: wp.i18n.__('Select Media', 'levcharity'), change: wp.i18n.__('Change media', 'levcharity')},
		 */
		mediaHeading: wp.i18n.__('Select Media', 'levcharity'),

		/**
		 * Indicates whether multiple media selection is allowed.
		 * @type {boolean}
		 */
		multipleSelect: false,

		/**
		 * Button text for selecting media or object with select and change text variations.
		 * @type {string|{select?: string, change?: string}}
		 *
		 * @example mediaSelectButtonText: {select: wp.i18n.__('Select', 'levcharity'), change: wp.i18n.__('Change', 'levcharity')}
		 */
		mediaSelectButtonText: wp.i18n.__('Select', 'levcharity'),

		/**
		 * Base64 encoded blank image or URL for default preview.
		 * @type {string}
		 */
		blankImage: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAQSURBVHgBAQUA+v8A5ubmvwjaA3KyfWQOAAAAAElFTkSuQmCC',

		/**
		 * Size of the attachment.
		 * @type {string}
		 */
		attachmentSize: 'medium_large',

		/**
		 * Whether to show selected media when the media selection dialog reopens.
		 * @type {boolean}
		 */
		showSelectedMediaOnReOpen: true,

		/**
		 * Whether to disable the remove button when media is not selected.
		 * @type {boolean}
		 */
		disableRemoveButton: true,

		/**
		 * Button text for selecting or changing media or object with select and change text variations.
		 * @type {string|{select?: string, change?: string}}
		 *
		 * @example buttonText: {select: wp.i18n.__('Select', 'levcharity'), change: wp.i18n.__('Change', 'levcharity')}
		 */
		buttonText: wp.i18n.__('Select', 'levcharity'),

		/**
		 * Callback function triggered when media selection dialog opens.
		 * @type {Function}
		 * @param {any} media The selected media.
		 */
		onOpen: function(media) {},

		/**
		 * Callback function triggered when media is selected.
		 * @type {Function}
		 * @param {any} media The selected media.
		 */
		onSelect: function(media) {},

		/**
		 * Callback function triggered when media is removed.
		 * @type {Function}
		 * @param {any} media The removed media.
		 */
		onRemove: function(media) {},

		/**
		 * Callback function triggered when a single media item is removed.
		 * @type {Function}
		 * @param {any} media The removed media.
		 * @param {number} index The index of the removed media item.
		 */
		onRemoveSingle: function(media, index) {},

		/**
		 * Callback function triggered when media selection dialog closes.
		 * @type {Function}
		 * @param {any} media The selected media.
		 */
		onClose: function(media) {}
	}

	/**
	 *
	 * WP Media object (wp.media)
	 *
	 * @type {object}
	 */
	#media;

	/**
	 * @type {Array<{}>|[]}
	 */
	attachments = [];


	/**
	 * @param {{
	 *  hiddenInput: (HTMLElement|null),
	 *  openButton: (HTMLElement|null),
	 *  removeButton: (HTMLElement|null),
	 *  previewElement: ({
	 *      container: (HTMLElement|null),
	 *      selector: string,
	 *      removeButtonSelector: string,
	 *      template: string
	 *  }|HTMLElement|null),
	 *  mediaHeading: (string|{select?: string, change?: string}),
	 *  multipleSelect: boolean,
	 *  mediaSelectButtonText: (string|{select?: string, change?: string}),
	 *  blankImage: string,
	 *  attachmentSize: string,
	 *  showSelectedMediaOnReOpen: boolean,
	 *  disableRemoveButton: boolean,
	 *  buttonText: (string|{select?: string, change?: string}),
	 *  onOpen: Function,
	 *  onSelect: Function,
	 *  onRemove: Function,
	 *  onRemoveSingle: Function,
	 *  onClose: Function
	 * }} args
	 */
	constructor(args) {
		super();
		if(args.dataAccept == '.json'){
			args.buttonText.change = wp.i18n.__('Select json');
			args.buttonText.select = wp.i18n.__('Select json');
		}
		this.#args = this.parseArgs(this.#defaultArgs, args);

		this.#initialize();
	}

	#initialize() {
		this.#initializeMedia();

		if (this.#args.hiddenInput) {
			const attachmentIds = this.#args.hiddenInput.value.split(',');
			const attachments = [];

			for (const attachmentId of attachmentIds) {
				const attachment = wp.media.attachment(attachmentId);
				attachment.fetch();
				attachments.push(attachment);
			}

			this.setAttachments(attachments);
		}

		this.#addEvents();
	}

	#initializeMedia() {
		let title = '';

		if (typeof this.#args.mediaHeading === 'object') {
			if (this.getAttachments().length && !this.#args.multipleSelect) {
				title = this.#args.mediaHeading.change
			} else {
				title = this.#args.mediaHeading.select;
			}
		} else if (typeof this.#args.mediaHeading === 'function') {
			title = this.#args.mediaHeading();
		} else {
			title = this.#args.mediaHeading;
		}

		let buttonText = '';

		if (typeof this.#args.mediaSelectButtonText === 'object') {
			if (this.getAttachments().length && !this.#args.multipleSelect) {
				buttonText = this.#args.mediaSelectButtonText.change
			} else {
				buttonText = this.#args.mediaSelectButtonText.select;
			}
		} else if (typeof this.#args.mediaSelectButtonText === 'function') {
			buttonText = this.#args.mediaSelectButtonText();
		} else {
			buttonText = this.#args.mediaSelectButtonText;
		}

		const dataAccept = [];

		if(this.#args.dataAccept){
			const dataAcceptString = this.#args.dataAccept;
			dataAccept.push(...dataAcceptString.split(',').map(type => type.trim()));
		}

		const mediaOptions = {
			title: title,
			multiple: this.#args.multipleSelect,
			button: {
				text: buttonText
			},
			dropZone: true
		};

		if (dataAccept.length > 0) {
			mediaOptions.library = { type: dataAccept };
		}

		this.#media = wp.media(mediaOptions);

		this.#media.on('open', () => {
			this.#onMediaOpen();
		});

		this.#media.on( 'select', () => {
			this.#onMediaSelect();
			this.#args.onSelect(this.#media);
		})

		this.#media.on( 'close', () => {
			this.#onMediaSelect();
			this.#args.onClose(this.#media);
		})
	}

	#addEvents() {
		if (this.#args.openButton){
			if (this.#args.openButton?.forEach) {
				this.#args.openButton.forEach(button => {
					button.addEventListener('click', (event) => {
						event.preventDefault();
						this.#open();
					})
				})
			} else {
				this.#args.openButton.addEventListener('click', (event) => {
					event.preventDefault();
					this.#open();
				})
			}

		}

		if (this.#args.removeButton) {
			if (this.#args.removeButton?.forEach) {
				this.#args.removeButton?.forEach(button => {
					button.addEventListener('click', (event) => {
						event.preventDefault();
						this.#remove();
					} )
				})
			} else {
				this.#args.removeButton.addEventListener('click', (event) => {
					event.preventDefault();
					this.#remove();
				} )
			}
		}

		if (this.#args.previewElement?.container) {
			this.#args.previewElement.container.addEventListener('click', (event) => {
				if (event.target.closest(this.#args.previewElement.removeButtonSelector)) {
					const button = event.target.closest(this.#args.previewElement.removeButtonSelector);
					const buttons = [...this.#args.previewElement.container.querySelectorAll(this.#args.previewElement.removeButtonSelector)];
					const index = buttons.indexOf(button)
					this.#removeSingular(index);
				}
			});
		}
	}


	#open() {
		this.#initializeMedia();

		this.#media.open();
		this.#args.onOpen(this.#media);
	}

	#remove() {
		this.attachments = [];
		this.save();
		this.#args.onRemove(this.#media);
	}

	#removeSingular(index) {
		this.attachments = this.attachments.slice(index, 0);
		this.save();
		this.#args.onRemoveSingle(this.#media, index);
	}

	#onMediaOpen() {
		const selection = this.#media.state().get('selection');

		if (this.#args.showSelectedMediaOnReOpen) {
			for (const attachmentId of this.getAttachments('id')) {
				const attachment = wp.media.attachment(attachmentId);
				attachment.fetch();
				selection.add(attachment);
			}
		}
	}

	#onMediaSelect() {
		const selection = this.#media.state().get( 'selection' ).toJSON();

		this.setAttachments(selection);

		this.save();
	}

	/**
	 *
	 * Get the attachments
	 *
	 * @param {string|null} field Key of the property of attachments
	 * @return {Array<{}>|[]}
	 */
	getAttachments(field = null) {
		const attachments = [];
		if (field) {
			for (const attachment of this.attachments) {
				attachments.push(attachment[field]);
			}

			return attachments;
		}

		return this.attachments;
	}


	/**
	 * Set the attachments
	 * @param {Array<{}>|[]} attachments
	 */
	setAttachments(attachments) {
		this.attachments = attachments;
	}


	/**
	 * Render a preview media items and save the data to the hidden input
	 *
	 * Based on the props given in <b>args</b> and saved values in the Media class
	 *
	 * @returns {void}
	 */
	save() {
		let buttonText = '';

		if (typeof this.#args.buttonText === 'object') {
			if (this.getAttachments().length && !this.#args.multipleSelect) {
				buttonText = this.#args.buttonText.change
			} else {
				buttonText = this.#args.buttonText.select;
			}
		} else if (typeof this.#args.buttonText === 'function') {
			buttonText = this.#args.buttonText();
		} else {
			buttonText = this.#args.buttonText;
		}

		if (buttonText) {
			this.#args.openButton.innerHTML = buttonText;
		}

		if (this.#args.removeButton && this.#args.disableRemoveButton) {
			if (!this.getAttachments().length) {
				this.#args.removeButton.disabled = true;
				this.#args.removeButton.setAttribute('aria-disabled', true);
				this.#args.removeButton.classList.add('disabled');
			} else {
				this.#args.removeButton.disabled = false;
				this.#args.removeButton.setAttribute('aria-disabled', false);
				this.#args.removeButton.classList.remove('disabled');
			}
		}

		if(this.#args.hiddenInput) {
			this.#args.hiddenInput.value = this.getAttachments('id').join(',');
		}

		if (this.#args.previewElement) {
			if (this.#args.previewElement?.container) {
				const attachments = this.getAttachments();
				const previewElement = this.#args.previewElement;

				previewElement.container.innerHTML = '';

				attachments.forEach((attachment, index) => {
					previewElement.container.innerHTML += this.#args.previewElement.template;
					const element = previewElement.container.children[index];

					element.classList.add('levcharity_multiple_media_item', `levcharity_multiple_media_item_${attachment.id}`);

					this.#setPreviewElementAttributes(element.querySelector(previewElement.selector), attachment);
				})

			} else {
				const attachment = this.getAttachments()[0];
				const previewElement = this.#args.previewElement;

				this.#setPreviewElementAttributes(previewElement, attachment);
			}
		}
	}

	/**
	 *
	 * @param {HTMLElement} element
	 * @param {Object} attachment
	 *
	 * @returns {void}
	 */
	#setPreviewElementAttributes(element, attachment) {
		if (!element) return;

		const attachmentSize = typeof attachment?.sizes[this.#args.attachmentSize] !== 'undefined' ? this.#args.attachmentSize : 'full';
		const size = attachment?.sizes[this.#args.attachmentSize] || attachment?.sizes?.full || null;
		element.src = size?.url || this.#args.blankImage;
		element.width = size?.width || 0;
		element.height = size?.height || 0;
		element.classList.add(`attachment-${attachmentSize}`, `size-${attachmentSize}`);
		element.alt = size?.url ? (attachment?.alt || attachment?.title || attachment?.filename || '') : wp.i18n.__('LevCharity Blank image', 'levcharity');
		element.style.objectFit = 'cover';
	}
}
